import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { submitEventbrellaProspect } from './submitEventbrellaProspect';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROI_DELAY_MS = 30_000;
const SESSION_SUBMITTED_KEY = 'eventbrella_talk_to_us_submitted';
const SESSION_SHOWN_KEY = 'eventbrella_talk_to_us_shown';
const SESSION_ROI_DUE_KEY = 'eventbrella_talk_to_us_roi_due';

const TalkToUsContext = createContext(null);

function readSessionFlag(key) {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(key) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

function readRoiDueAt() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = window.sessionStorage.getItem(SESSION_ROI_DUE_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeRoiDueAt(dueAt) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SESSION_ROI_DUE_KEY, String(dueAt));
  } catch {
    /* ignore */
  }
}

function clearRoiDueAt() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SESSION_ROI_DUE_KEY);
  } catch {
    /* ignore */
  }
}

export function TalkToUsProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const openTalkToUs = useCallback(() => {
    if (readSessionFlag(SESSION_SUBMITTED_KEY)) return;
    writeSessionFlag(SESSION_SHOWN_KEY);
    setIsOpen(true);
  }, []);

  const closeTalkToUs = useCallback(() => {
    setIsOpen(false);
  }, []);

  const markSubmitted = useCallback(() => {
    writeSessionFlag(SESSION_SUBMITTED_KEY);
    writeSessionFlag(SESSION_SHOWN_KEY);
    clearRoiDueAt();
    setIsOpen(false);
  }, []);

  const tryOpenFromDelayedRoi = useCallback(() => {
    clearRoiDueAt();
    if (isOpenRef.current) return;
    if (readSessionFlag(SESSION_SUBMITTED_KEY)) return;
    if (readSessionFlag(SESSION_SHOWN_KEY)) return;
    writeSessionFlag(SESSION_SHOWN_KEY);
    setIsOpen(true);
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const armRoiTimer = useCallback(
    (dueAt) => {
      clearTimer();
      if (!dueAt) return;
      if (readSessionFlag(SESSION_SUBMITTED_KEY) || readSessionFlag(SESSION_SHOWN_KEY)) {
        clearRoiDueAt();
        return;
      }
      const delay = Math.max(0, dueAt - Date.now());
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        tryOpenFromDelayedRoi();
      }, delay);
    },
    [clearTimer, tryOpenFromDelayedRoi]
  );

  const scheduleTalkToUsFromRoi = useCallback(() => {
    if (readSessionFlag(SESSION_SUBMITTED_KEY) || readSessionFlag(SESSION_SHOWN_KEY)) {
      return;
    }
    if (isOpenRef.current) return;
    const dueAt = Date.now() + ROI_DELAY_MS;
    writeRoiDueAt(dueAt);
    armRoiTimer(dueAt);
  }, [armRoiTimer]);

  useEffect(() => {
    armRoiTimer(readRoiDueAt());
    return clearTimer;
  }, [armRoiTimer, clearTimer]);

  const value = {
    isOpen,
    openTalkToUs,
    closeTalkToUs,
    markSubmitted,
    scheduleTalkToUsFromRoi,
  };

  return (
    <TalkToUsContext.Provider value={value}>
      {children}
    </TalkToUsContext.Provider>
  );
}

export function useTalkToUs() {
  const ctx = useContext(TalkToUsContext);
  if (!ctx) {
    throw new Error('useTalkToUs must be used within TalkToUsProvider');
  }
  return ctx;
}

function TalkToUsPopup() {
  const { isOpen, closeTalkToUs, markSubmitted } = useTalkToUs();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setPhone('');
      setEmail('');
      setError('');
      setSubmitting(false);
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    const result = await submitEventbrellaProspect({
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone || undefined,
    });
    setSubmitting(false);

    if (!result.ok) {
      setError(
        result.error === 'not_configured'
          ? 'Something went wrong. Please try again later.'
          : 'Something went wrong. Please try again.'
      );
      return;
    }

    markSubmitted();
  };

  if (!isOpen) return null;

  return (
    <div
      className="eb-talk-overlay"
      role="presentation"
      onClick={closeTalkToUs}
    >
      <div
        className="eb-talk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eb-talk-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="eb-talk-close"
          onClick={closeTalkToUs}
          aria-label="Close"
        >
          ×
        </button>
        <span className="eyebrow">Talk to Us</span>
        <h2 id="eb-talk-title">Tell us how to reach you</h2>
        <p className="eb-talk-sub">
          Leave your details and we&apos;ll follow up about bringing EventBrella to your next event.
        </p>
        <form className="eb-talk-form" onSubmit={handleSubmit} noValidate>
          <label className="eb-talk-field">
            <span>Name <em>*</em></span>
            <input
              type="text"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <label className="eb-talk-field">
            <span>Phone number</span>
            <input
              type="tel"
              name="phone"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="eb-talk-field">
            <span>Email <em>*</em></span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          {error ? <p className="eb-talk-error" role="alert">{error}</p> : null}
          <button type="submit" className="btn btn-gold" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default TalkToUsPopup;
